/**
 * Browser OTLP log export (PRD-3050).
 *
 * Sends structured portal logger records to the same-origin `/otlp/v1/logs`
 * endpoint. Local dev forwards via the Vite proxy; deployed envs require
 * ingress configuration by CloudOps.
 *
 * @see https://lyt.atlassian.net/wiki/spaces/CLOUD/pages/903610403
 */
import { logs, SeverityNumber } from '@opentelemetry/api-logs';
import { OTLPLogExporter } from '@opentelemetry/exporter-logs-otlp-http';
import { resourceFromAttributes } from '@opentelemetry/resources';
import { BatchLogRecordProcessor, LoggerProvider } from '@opentelemetry/sdk-logs';

import { env } from 'src/config/env';
import { getApplicationRelease } from 'src/config/applicationRelease';
import { toOtlpLogAttributes } from 'src/observability/otlp-log-attributes';
import { resolveOtlpEventType } from 'src/observability/resolve-otlp-log-eligibility';

let loggerProvider = null;
let otelLogger = null;

const LEVEL_TO_SEVERITY = Object.freeze({
    debug: { number: SeverityNumber.DEBUG, text: 'DEBUG' },
    info: { number: SeverityNumber.INFO, text: 'INFO' },
    warn: { number: SeverityNumber.WARN, text: 'WARN' },
    error: { number: SeverityNumber.ERROR, text: 'ERROR' },
});

/**
 * @param {string} level
 * @returns {{ number: number, text: string }}
 */
const mapLevelToSeverity = (level) =>
    LEVEL_TO_SEVERITY[level] || LEVEL_TO_SEVERITY.info;

/**
 * Initialize OTLP log export when running in a browser. Safe no-op in SSR/tests.
 *
 * @returns {void}
 */
export const initializeOtlpLogs = () => {
    if (typeof window === 'undefined' || loggerProvider) {
        return;
    }

    try {
        const exporter = new OTLPLogExporter({
            url: `${window.location.origin}/otlp/v1/logs`,
        });

        const provider = new LoggerProvider({
            resource: resourceFromAttributes({
                'service.name': env.otelServiceName,
                'deployment.environment.name': env.environment || 'unknown',
                'service.version': getApplicationRelease(),
                'service.namespace': env.otelServiceNamespace,
            }),
        });

        provider.addLogRecordProcessor(new BatchLogRecordProcessor(exporter));
        logs.setGlobalLoggerProvider(provider);
        otelLogger = logs.getLogger(env.otelServiceName);
        loggerProvider = provider;
    } catch (error) {
        console.error('OTLP logs initialization failed:', error);
        loggerProvider = null;
        otelLogger = null;
    }
};

/**
 * Emit one structured log record to OTLP when initialized.
 *
 * @param {Object} record
 * @param {string} record.level
 * @param {string} record.message
 * @param {string} [record.eventType]
 * @param {Object} [record.meta]
 */
export const emitOtlpLog = (record) => {
    if (!otelLogger || !record) {
        return;
    }

    try {
        const eventType = resolveOtlpEventType(record);
        const { session_id: sessionId, ...restMeta } = record.meta || {};
        const severity = mapLevelToSeverity(record.level);

        const attributes = toOtlpLogAttributes({
            ...restMeta,
            level: record.level,
            ...(eventType ? { 'com.lyt.event_type': eventType } : {}),
            ...(sessionId ? {
                session_id: sessionId,
                'session.id': sessionId,
            } : {}),
        });

        otelLogger.emit({
            severityNumber: severity.number,
            severityText: severity.text,
            body: record.message == null ? '' : String(record.message),
            attributes,
        });
    } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
            console.error('OTLP log export failed:', error);
        }
    }
};

/**
 * @returns {boolean}
 */
export const isOtlpLogsInitialized = () => Boolean(otelLogger);

/**
 * Test helper — clears the module singleton between tests.
 */
export const __resetOtlpLogsForTests = () => {
    loggerProvider = null;
    otelLogger = null;
};
